import { Controller } from "@hotwired/stimulus"

// HubSpot deal search picker. Debounced search hits our server-side proxy
// (which calls the HubSpot API); what happens on select depends on `mode`:
//   "link"    (show page)  — submits the hidden link form with the deal id.
//   "prefill" (new form)   — writes the deal id into the opportunity form and
//                            fetches deal+company+contact details to prefill
//                            any empty business fields.
export default class extends Controller {
  static targets = [
    "query", "status", "results", "form", "dealId", "searchPanel",
    "selectedSummary", "selectedName",
    "conflictPanel", "conflictDealName", "conflictReference",
    "conflictOpportunityName", "conflictLink"
  ]
  static values = {
    searchUrl: String,
    mode: { type: String, default: "link" },
    debounceMs: { type: Number, default: 350 },
    opportunityShowPath: { type: String, default: "/sales/opportunities" },
    excludeOpportunityId: { type: String, default: "" }
  }

  scheduleSearch() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.search(), this.debounceMsValue)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  async search() {
    const q = this.queryTarget.value.trim()
    if (q.length < 2) {
      this.clearResults()
      this.statusTarget.textContent = "Type at least 2 characters"
      return
    }

    this.statusTarget.textContent = "Searching HubSpot..."
    try {
      const params = new URLSearchParams({ q })
      if (this.excludeOpportunityIdValue) {
        params.set("exclude_opportunity_id", this.excludeOpportunityIdValue)
      }
      const url = `${this.searchUrlValue}?${params}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
      this.renderResults(data.deals || [])
    } catch (err) {
      this.statusTarget.textContent = `Search failed: ${err.message}`
    }
  }

  renderResults(deals) {
    if (deals.length === 0) {
      this.clearResults()
      this.statusTarget.textContent = "No matching deals"
      return
    }
    this.statusTarget.textContent = `${deals.length} result(s)`
    this.resultsTarget.classList.remove("d-none")

    this.resultsTarget.innerHTML = deals.map(d => {
      if (d.opportunity_id) {
        const ref = d.opportunity_reference ? `${d.opportunity_reference} · ` : ""
        const url = `${this.opportunityShowPathValue}/${d.opportunity_id}`
        return `
          <div class="list-group-item bg-light">
            <div class="fw-semibold text-muted">${this.esc(d.dealname || `Deal ${d.id}`)}</div>
            <div class="small">
              Already linked to ${this.esc(ref)}${this.esc(d.opportunity_name || "an opportunity")}
              <a href="${this.esc(url)}" class="ms-1">View</a>
            </div>
          </div>
        `
      }

      return `
      <button type="button" class="list-group-item list-group-item-action"
              data-action="click->sales-hubspot-deal-picker#select"
              data-deal-id="${this.esc(d.id)}"
              data-deal-name="${this.esc(d.dealname || `Deal ${d.id}`)}">
        <div class="fw-semibold">${this.esc(d.dealname || `Deal ${d.id}`)}</div>
        <div class="text-muted small">
          ${d.dealstage ? this.esc(this.humanize(d.dealstage)) : ""}
          ${d.amount ? ` · $${this.esc(d.amount)}` : ""}
          ${d.closedate ? ` · closes ${this.esc(d.closedate.slice(0, 10))}` : ""}
        </div>
      </button>
    `
    }).join("")
  }

  select(event) {
    const { dealId, dealName } = event.currentTarget.dataset
    this.hideConflict()
    this.dealIdTarget.value = dealId

    if (this.modeValue === "prefill") {
      this.showSelected(dealName)
      this.prefill(dealId)
      this.dispatch("linked", { detail: { dealId, dealName } })
    } else {
      this.formTarget.requestSubmit()
    }
  }

  selectTaken(event) {
    const deal = JSON.parse(
      event.currentTarget.dataset.deal.replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    )

    this.dealIdTarget.value = ""
    this.hideSelected()
    this.clearResults()
    this.queryTarget.value = ""
    this.statusTarget.textContent = ""
    this.hideSearch()
    this.showConflict(deal)
    this.dispatch("conflict", { detail: { deal } })
  }

  clear() {
    this.dealIdTarget.value = ""
    this.hideSelected()
    this.hideConflict()
    this.clearResults()
    this.queryTarget.value = ""
    this.statusTarget.textContent = ""
    this.showSearch()
    this.dispatch("cleared")
  }

  clearConflict() {
    this.hideConflict()
    this.clearResults()
    this.queryTarget.value = ""
    this.statusTarget.textContent = ""
    this.showSearch()
    this.dispatch("cleared")
  }

  showConflict(deal) {
    if (this.hasConflictDealNameTarget) {
      this.conflictDealNameTarget.textContent = deal.dealname || `Deal ${deal.id}`
    }
    if (this.hasConflictReferenceTarget) {
      this.conflictReferenceTarget.textContent = deal.opportunity_reference
        ? `${deal.opportunity_reference} · `
        : ""
    }
    if (this.hasConflictOpportunityNameTarget) {
      this.conflictOpportunityNameTarget.textContent = deal.opportunity_name || "Open opportunity"
    }
    if (this.hasConflictLinkTarget) {
      this.conflictLinkTarget.href = `${this.opportunityShowPathValue}/${deal.opportunity_id}`
    }
    if (this.hasConflictPanelTarget) this.conflictPanelTarget.classList.remove("d-none")
  }

  hideConflict() {
    if (this.hasConflictPanelTarget) this.conflictPanelTarget.classList.add("d-none")
  }

  showSelected(name) {
    if (this.hasSelectedNameTarget) this.selectedNameTarget.textContent = name
    if (this.hasSelectedSummaryTarget) {
      this.selectedSummaryTarget.classList.remove("d-none")
      this.selectedSummaryTarget.classList.add("d-flex")
    }
    this.clearResults()
    this.queryTarget.value = ""
    this.hideSearch()
  }

  hideSearch() {
    if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.add("d-none")
  }

  showSearch() {
    if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.remove("d-none")
  }

  clearResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("d-none")
  }

  hideSelected() {
    if (this.hasSelectedSummaryTarget) {
      this.selectedSummaryTarget.classList.add("d-none")
      this.selectedSummaryTarget.classList.remove("d-flex")
    }
  }

  async prefill(dealId) {
    this.statusTarget.textContent = "Fetching deal details from HubSpot..."
    try {
      const response = await fetch(`${this.searchUrlValue}/${dealId}`, { headers: { Accept: "application/json" } })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)

      const p = data.prefill || {}
      this.fill("sales_opportunity[business_name]", p.business_name)
      this.fill("sales_opportunity[point_of_contact]", p.point_of_contact)
      this.fill("sales_opportunity[email]", p.email)
      this.fill("sales_opportunity[phone_number]", p.phone_number)
      this.fill("sales_opportunity[website]", p.website)
      this.fillSelect("sales_opportunity[industry]", p.industry)
      this.fillSelect("sales_opportunity[current_accounting_system]", p.current_accounting_system)
      this.fillSelect("sales_opportunity[proposed_accounting_system]", p.proposed_accounting_system)
      this.fillSelect("sales_opportunity[lead_source]", p.lead_source)
      this.statusTarget.textContent = "Business details filled from HubSpot — review and complete the rest."
    } catch (err) {
      this.statusTarget.textContent = `Couldn't fetch deal details: ${err.message}`
    }
  }

  fill(name, value) {
    if (!value) return
    const el = document.querySelector(`input[name="${name}"], textarea[name="${name}"]`)
    if (el && !el.value) el.value = value
  }

  fillSelect(name, value) {
    if (!value) return
    const el = document.querySelector(`select[name="${name}"], select[name="${name}[]"]`)
    if (!el) return
    const tokens = String(value).split(";").map(token => token.trim()).filter(Boolean)
    const matched = tokens
      .map(token => Array.from(el.options).find(option => option.value.toLowerCase() === token.toLowerCase())?.value)
      .filter(Boolean)
    if (!matched.length) return
    if (el.multiple) {
      if (el.tomselect) {
        if (el.tomselect.getValue().length) return
        el.tomselect.setValue(matched)
      } else if (!Array.from(el.selectedOptions).length) {
        Array.from(el.options).forEach(option => { option.selected = matched.includes(option.value) })
      }
      return
    }
    if (el.value) return
    el.value = matched[0]
  }

  humanize(str) {
    return String(str).replace(/[_-]+/g, " ").replace(/^./, c => c.toUpperCase())
  }

  esc(str) {
    return String(str).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
